import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { LogIn, UserPlus } from 'lucide-react'
import { AuthShell } from './auth/AuthShell'
import { ActivateCodeForm } from '@/components/ActivateCodeForm'
import { Button, ButtonLink, InfoNote } from '@/components/ui'
import { FullPageSpinner } from '@/components/FullPageSpinner'
import { useAuth } from '@/lib/auth'
import { useFeatures } from '@/lib/features'
import { formatCode, normalizeCode } from '@shared/codes'

/**
 * Sidan QR-koden på den fysiska lappen leder till.
 *
 * Koden följer med i länken, så köparen slipper knappa in den. Är hen inte
 * inloggad ännu skickas koden vidare genom registreringen och fylls i igen
 * på andra sidan.
 */
export function ActivatePage() {
  const [searchParams] = useSearchParams()
  const { user, loading } = useAuth()
  const navigate = useNavigate()
  const { features } = useFeatures()

  const rawCode = searchParams.get('kod') ?? ''
  const normalized = normalizeCode(rawCode)
  const pretty = normalized ? formatCode(normalized) : rawCode

  if (loading) return <FullPageSpinner />

  if (!user) {
    const next = normalized ? `?kod=${encodeURIComponent(pretty)}` : ''
    return (
      <AuthShell
        title="Aktivera ditt Lirahäfte"
        intro={
          features.signUp
            ? 'Du behöver ett konto för att spara häftet. Det tar en halv minut.'
            : 'Du behöver ett konto för att spara häftet.'
        }
      >
        <div className="space-y-4">
          {normalized && (
            <div className="rounded-card border-2 border-dashed border-brand/40 bg-brand-soft px-4 py-3 text-center">
              <p className="text-xs font-bold tracking-wide text-muted uppercase">Din kod</p>
              <p className="font-mono text-xl font-bold tracking-[0.2em] text-brand-ink">{pretty}</p>
            </div>
          )}

          {/* Innan lanseringen leder knappen till en spärrad sida. Då är det
              bättre att säga varför än att låta någon trycka och studsa. */}
          {features.signUp && (
            <ButtonLink
              to={`/skapa-konto${next}`}
              size="lg"
              fullWidth
              icon={<UserPlus size={18} />}
            >
              Skapa konto
            </ButtonLink>
          )}

          <ButtonLink
            to={`/logga-in${next}`}
            size="lg"
            fullWidth
            variant={features.signUp ? 'outline' : 'primary'}
            icon={<LogIn size={18} />}
          >
            Jag har redan ett konto
          </ButtonLink>

          <InfoNote>
            {features.signUp
              ? 'Spara koden tills häftet är aktiverat. Efter aktivering hör häftet till ditt konto och lappen behövs inte längre.'
              : 'Appen är inte öppen än, så det går inte att skapa konto. Spara lappen – koden gäller, och du aktiverar den här så fort häftet släpps.'}
          </InfoNote>
        </div>
      </AuthShell>
    )
  }

  return (
    <AuthShell title="Aktivera ditt Lirahäfte">
      <div className="space-y-4">
        <ActivateCodeForm initialCode={pretty} onActivated={() => navigate('/', { replace: true })} />
        <Button variant="ghost" fullWidth onClick={() => navigate('/')}>
          Hoppa över just nu
        </Button>
        <p className="text-center text-sm text-muted">
          Behöver du hjälp?{' '}
          <Link to="/hjalp" className="underline underline-offset-2">
            Läs mer här
          </Link>
        </p>
      </div>
    </AuthShell>
  )
}
